import React from 'react';
import proptypes from 'prop-types';
import {
  Typography, SkeletonBlock, Skeleton,
  CardMedia, Card as StencilCard, CardContent
} from '@one-thd/sui-atomic-components';
import { getPaintContainerSize, getPaintImageUrl } from '../../super-sku-util';
import { CardPrice } from './components/CardPrice';

const {
  bool, func, shape, string,
} = proptypes;

const renderCardImage = ({
  isSheen, showImage, imageUrl, hexColor, attributeValue, containerSize, paintType, attributeName,
}) => {
  const isBrandName = attributeName === 'Brand Name';

  const imageContentStyles = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: containerSize ? '32px' : '16px',
    margin: paintType ? '16px' : undefined,
    flex: isBrandName ? '0 0 auto' : '1',
    overflow: 'hidden',
  };

  if (isSheen) {
    return (
      <CardContent disablePadding>
        <CardMedia
          src={imageUrl}
          style={hexColor ? { background: `#${hexColor}` } : undefined}
          alt={attributeValue}
          width="full"
          aspect="square"
          padding={false}
          variant="border"
        />
      </CardContent>
    );
  }

  if (showImage) {
    return (
      <CardContent style={imageContentStyles}>
        <CardMedia
          src={imageUrl}
          alt={attributeValue}
          aspect="square"
          width="full"
          style={{ objectFit: 'contain', width: '100%', height: 'auto' }}
        />
      </CardContent>
    );
  }
  return null;
};

const renderCardInfo = ({
  isSheen, attributeValue, pricing, attributeName

}) => {
  const infoStyles = {
    flex: !isSheen ? '0 0 12px' : undefined,
  };
  return (
    <div className="sui-p-1 sui-flex sui-flex-col sui-justify-center" style={infoStyles}>
      <Typography
        component="p"
        align="center"
        height="tight"
        variant={attributeName === 'Brand Name' ? 'body-xs' : 'body-sm'}
      >
        {attributeValue}
      </Typography>
      {pricing && <CardPrice pricing={pricing} />}
    </div>
  );
};

export const Card = (props) => {
  const {
    isDotComColorEligible,
    isSelected,
    attributeValue,
    onClick,
    swatchCardCombinationDetails,
    centeredCards,
    hexColor,
    attributeName,
    paintBrand,
    isPaintReorder,
  } = props;

  const { image, pricing } = swatchCardCombinationDetails || {};

  let imageUrl = image;
  let isSheen;
  let containerSize;
  if (isDotComColorEligible || isPaintReorder) {
    imageUrl = getPaintImageUrl(attributeValue, attributeName, paintBrand);
    isSheen = attributeName === 'Sheen';
    containerSize = getPaintContainerSize(attributeValue);
  }
  const NO_IMAGE_ATTRIBUTES = ['Sheen', 'Brand Name', 'Color/Finish', 'Paint Type'];
  const showImage = imageUrl || !NO_IMAGE_ATTRIBUTES.includes(attributeName);

  const hasCombinationDetails = Object.keys(swatchCardCombinationDetails || {}).length !== 0;
  const loading = hasCombinationDetails && !imageUrl && showImage;
  const paintType = attributeName === 'Paint Type';

  if (loading) {
    return (
      <Skeleton data-testid="card-loader">
        <SkeletonBlock aspect="square" />
      </Skeleton>
    );
  }

  if (!hasCombinationDetails) {
    return null;
  }

  const cardContentStyles = {
    gap: 0,
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  };

  const cardAriaLabel = attributeName
    ? `${attributeName}: ${attributeValue}`
    : attributeValue;

  return (
    <StencilCard
      component="button"
      onClick={onClick}
      aria-label={cardAriaLabel}
      disablePadding
      CardContentProps={{
        style: cardContentStyles,
      }}
      PaperProps={{
        style: {
          height: '100%',
          boxShadow: 'none',
          border: isSelected
            ? 'solid 2px rgb(249, 99, 2)'
            : '1px solid var(--sui-color-border-primary, #d9d9d9)',
        },
      }}
      variant="border"
    >
      {renderCardImage({
        isSheen, showImage, imageUrl, hexColor, attributeValue, containerSize, paintType, attributeName,
      })}
      {renderCardInfo({
        isSheen, attributeValue, pricing, attributeName
      })}

    </StencilCard>
  );
};

Card.displayName = 'Card';

Card.propTypes = {
  attributeValue: string.isRequired,
  centeredCards: bool,
  hexColor: string,
  isDotComColorEligible: bool,
  isSelected: bool,
  onClick: func.isRequired,
  swatchCardCombinationDetails: shape({
    image: string,
    pricing: shape({
      message: string,
      isSpecialPrice: bool,
      strikeThroughPriceForMap: bool,
      price: shape({
        formatted: string,
        dollars: string,
        cents: string,
        lessThanDollar: bool,
      }),
    }),
  }).isRequired,
  attributeName: string,
  paintBrand: string,
  isPaintReorder: bool,
};

Card.defaultProps = {
  centeredCards: false,
  hexColor: '',
  isDotComColorEligible: false,
  isSelected: false,
  attributeName: '',
  isPaintReorder: false,
  paintBrand: '',
};